import dayjs from 'dayjs'

export type PaymentsRetentionTrendMonth = {
  month: string
  switchedTenants: number
  invoicingTenants: number
  onSynkedUpTenants: number
  revertedTenants: number
  idleTenants: number
  onSynkedUpPct: number | null
  synkedUpVolume: number
  totalVolume: number
  synkedUpVolumePct: number | null
}

export type PaymentsRetentionCohortMonth = {
  monthsSinceSwitch: number
  onSynkedUpTenants: number
  revertedTenants: number
  idleTenants: number
  onSynkedUpPct: number | null
}

export type PaymentsRetentionCohort = {
  switchMonth: string
  tenantCount: number
  months: PaymentsRetentionCohortMonth[]
}

export type RevertedTenant = {
  tenantId: number
  tenantName: string
  switchedAt: string
  lastSynkedUpInvoiceAt: string
  lastQuickBooksInvoiceAt: string
  currentInvoicingSystem: string | null
}

export type PaymentsRetentionData = {
  switcherCount: number
  trend: PaymentsRetentionTrendMonth[]
  cohorts: PaymentsRetentionCohort[]
  revertedTenants: RevertedTenant[]
  generatedAt: string
}

export const defaultPaymentsRetentionTrendMonth = (): PaymentsRetentionTrendMonth => ({
  month: '2026-01-01',
  switchedTenants: 0,
  invoicingTenants: 0,
  onSynkedUpTenants: 0,
  revertedTenants: 0,
  idleTenants: 0,
  onSynkedUpPct: null,
  synkedUpVolume: 0,
  totalVolume: 0,
  synkedUpVolumePct: null,
})

export const defaultPaymentsRetentionCohortMonth = (): PaymentsRetentionCohortMonth => ({
  monthsSinceSwitch: 0,
  onSynkedUpTenants: 0,
  revertedTenants: 0,
  idleTenants: 0,
  onSynkedUpPct: null,
})

export const formatRetentionMonth = (month: string): string => dayjs(month).format('MMM YYYY')

export const formatRetentionDate = (value: string): string => dayjs(value).format('MMM D, YYYY')

export const formatRetentionPct = (pct: number | null): string => {
  if (pct === null) {
    return '—'
  }
  return `${pct.toFixed(1)}%`
}

export type RetentionTrendPoint = {
  month: string
  label: string
  onSynkedUpPct: number | null
  synkedUpVolumePct: number | null
}

export const buildTrendPoints = (trend: PaymentsRetentionTrendMonth[]): RetentionTrendPoint[] =>
  trend.map((m) => ({
    month: m.month,
    label: formatRetentionMonth(m.month),
    onSynkedUpPct: m.onSynkedUpPct,
    synkedUpVolumePct: m.synkedUpVolumePct,
  }))

export const cohortColumnCount = (cohorts: PaymentsRetentionCohort[]): number =>
  cohorts.reduce((max, c) => Math.max(max, c.months.length), 0)

export const findCohortMonth = (
  cohort: PaymentsRetentionCohort,
  monthsSinceSwitch: number,
): PaymentsRetentionCohortMonth | undefined =>
  cohort.months.find((m) => m.monthsSinceSwitch === monthsSinceSwitch)

const COHORT_SHADES = ['#cde2fb', '#b7d3f6', '#9ec5f4', '#86b6ef']

export const cohortCellShade = (pct: number | null): string | undefined => {
  if (pct === null) {
    return undefined
  }
  const bucket = Math.min(COHORT_SHADES.length - 1, Math.floor(pct / (100 / COHORT_SHADES.length)))
  return COHORT_SHADES[Math.max(0, bucket)]
}

export const describeCohortMonth = (month: PaymentsRetentionCohortMonth): string =>
  `On SynkedUP ${month.onSynkedUpTenants} · Reverted ${month.revertedTenants} · Idle ${month.idleTenants}`

export const describeInvoicingSystem = (system: string | null): string => system ?? 'Not set'
